import { Key } from 'react';
import { COLUMN_MIN_WIDTH, EXPAND_COLUMN_WIDTH } from './constants';
import {
  DataTableColumnsType,
  DataTableColumnGroupType,
  DataTableColumnType,
  DataTableRenderExpandedRow,
  DataTableRowType,
  DataTableSelectableRows,
} from './types';

export interface UseTableColumnsProps<DataType> {
  columns: DataTableColumnsType<DataType>;
}

/**
 * 컬럼/그룹 컬럼을 평탄화해 colgroup 렌더용 `{ id?, width?, minWidth? }` 배열로 반환한다.
 * - 그룹 컬럼은 하위 컬럼으로 펼치고, 일반 컬럼은 그대로 매핑한다.
 */
export function getColGroup<DataType>({
  columns,
}: UseTableColumnsProps<DataType>): Array<{ id?: string; width?: number; minWidth?: number }> {
  // @ts-expect-error: TS2769
  return columns.reduce((accumulator, column) => {
    const baseColumn = column as DataTableColumnType<DataType>;
    const columnGroup = (column as DataTableColumnGroupType<DataType>).columnGroup;

    return [
      ...accumulator,
      ...(columnGroup && columnGroup.length > 0
        ? columnGroup.map((subColumn) => {
            const subColumGroup = subColumn as DataTableColumnType<DataType>;
            return {
              id: subColumGroup.id,
              width:
                subColumGroup.width ??
                (subColumGroup.width ? subColumGroup.width ?? 0 / columnGroup.length : undefined),
              minWidth:
                subColumGroup.minWidth ??
                (subColumGroup.minWidth ? subColumGroup.minWidth ?? 0 / columnGroup.length : undefined),
            };
          })
        : [{ id: baseColumn.id, width: baseColumn.width, minWidth: baseColumn.minWidth }]),
    ];
  }, []);
}

export function getGroupColumns<DataType>({ columns }: UseTableColumnsProps<DataType>): DataTableColumnsType<DataType> {
  // @ts-expect-error: TS2769
  return columns.reduce((accumulator, column) => {
    const groupColumns = (column as DataTableColumnGroupType<DataType>).columnGroup;
    if (groupColumns && groupColumns.length > 0) {
      return [...accumulator, ...(groupColumns ?? [])];
    }
    return accumulator;
  }, []);
}

export function getFlattenColumns<DataType>({
  columns,
}: UseTableColumnsProps<DataType>): DataTableColumnsType<DataType> {
  // @ts-expect-error: TS2769
  return columns.reduce((accumulator, column) => {
    const groupColumns = (column as DataTableColumnGroupType<DataType>).columnGroup;
    if (groupColumns && groupColumns.length > 0) {
      return [
        ...accumulator,
        ...getFlattenColumns({ columns: groupColumns }).map((groupColumn) => ({
          ...groupColumn,
        })),
      ];
    }
    return [...accumulator, { ...column }];
  }, []);
}

interface GetTotalColumnCountProps<DataType> extends UseTableColumnsProps<DataType> {
  selectableRows?: DataTableSelectableRows<DataType>;
  hasExpandButton?: boolean;
  renderExpandedRow?: DataTableRenderExpandedRow<DataType>;
}

export function getTotalColumnCount<DataType>({
  columns,
  selectableRows,
  hasExpandButton,
  renderExpandedRow,
}: GetTotalColumnCountProps<DataType>): number {
  return (
    getFlattenColumns({ columns }).length + (selectableRows ? 1 : 0) + (hasExpandButton && renderExpandedRow ? 1 : 0)
  );
}

export function getFixedMergeColumn<DataType>(
  columns: DataTableColumnsType<DataType>,
  selectableRows: boolean,
  columnCount: number,
  renderExpandedRow: boolean,
): number {
  // @ts-expect-error: TS2345
  const columnWidths = columns.map((col: DataTableColumnType<DataType>) => col.width);

  const baseWidth = selectableRows ? COLUMN_MIN_WIDTH : 0;
  const expandWidth = renderExpandedRow ? EXPAND_COLUMN_WIDTH : 0;

  if (columnCount === 0) {
    return baseWidth + expandWidth;
  }

  if (columns[columnCount - 1]?.fixed) {
    const totalFixedColumnWidths = getFixedColumnWidth(columnWidths, columnCount);
    const mergedWidth =
      totalFixedColumnWidths.length > 0 ? totalFixedColumnWidths.reduce((acc, cur) => acc + cur, 0) : 0;
    return mergedWidth + baseWidth + expandWidth;
  }

  return 0;
}

function getFixedColumnWidth(colWidths: (number | undefined)[], columCount: number): number[] {
  const cloneColumns: number[] = [];
  for (let i = 0; i < columCount; i += 1) {
    const val = colWidths[i];
    if (val !== undefined) {
      cloneColumns[i] = val;
    }
  }

  return cloneColumns;
}

export function defaultRowSelectionKeyExtractor(row: DataTableRowType<unknown>): Key {
  if (row.key == null) {
    throw new Error(
      'Selection/Expandable 테이블 로우 데이터는 `key`가 존재해야 합니다. (row.key 혹은 getRowSelectionKey를 지정해주세요)',
    );
  }
  return row.key;
}

export function defaultRowKeyExtractor(row: DataTableRowType<unknown>, rowIndex: number): Key {
  return rowIndex;
}
